/**
 * ArchLang Core Types (v0.2)
 *
 * The AST/CST type definitions. These mirror the grammar in
 * docs/GRAMMAR.md as closely as possible.
 *
 * Key v0.2 concepts:
 * - Bodies (module / surface / type) share a common shape: fields, aspects,
 *   descriptions, drops, and three categories of sub-declarations
 *   (surfaces, interfaces, modules). Module body and type body differ only
 *   in which modifiers can appear (e.g., `required`, `cascade`, `append`
 *   are type-only).
 * - Interfaces are leaves; their bodies have fields/aspects/descriptions,
 *   but no nested declarations.
 * - Stable IDs are carried only by modules, types, processes, and views.
 *   Surfaces and interfaces get identity from their dot-path within a
 *   stable parent module.
 * - There is no `extends` keyword — type subtyping is expressed by the
 *   parent-kind position in `type <parent> <name>`.
 */

// v0.10 governance/query declarations (types-only; erased at build → no cycle
// with v010-ast.ts, which imports FieldDecl/Name/… back from here).
import type { QueryDecl, PolicyDecl, StyleBundleDecl, LensDecl, TestDecl, UsePolicyStmt, ViewDeclV2 } from "./v010-ast.js";
// The expression grammar shared by views' `style` bodies and field values
// (types-only, same no-cycle reason as above).
import type { Expr } from "./selector-ast.js";
import type { DecorationDecl, DecorationInstance } from "./decorations.js";
import type { DiagnosticCode, DiagnosticEvidence } from "./diagnostics/codes.js";

// =============================================================================
// Identifiers
// =============================================================================

/**
 * System-generated stable identifier. Opaque alphanumeric suffix that
 * survives every kind of rename. Format: `#xyz` (the `#` is part of the
 * source token; the `StableId` value here is the suffix only).
 */
export type StableId = string & { readonly __brand: "StableId" };

/** Human-readable name (refactorable). */
export type Name = string;

/**
 * A qualified reference: `["Orders", "OrdersResource", "Create"]`.
 * Stored as an array so consumers don't have to re-split.
 */
export type QualifiedName = readonly Name[];

// =============================================================================
// Source spans
// =============================================================================

export interface Position {
  readonly line: number;
  readonly column: number;
  readonly offset: number;
}

export interface SourceSpan {
  readonly start: Position;
  readonly end: Position;
  readonly file?: string;
}

// =============================================================================
// Values
// =============================================================================

/**
 * The complete set of value kinds a field or aspect can take. Identifiers
 * and strings are kept distinct so tooling can render references vs.
 * literals differently.
 */
export type Value =
  | { readonly kind: "identifier"; readonly text: string; readonly source?: SourceSpan }
  | {
      /** v0.9.0 §1.3: an aspect membership reference — a (possibly dotted)
       *  module/place path, optionally instance-named (`Euro.DC1 as active`).
       *  A single bare segment without `as` parses as plain `identifier`. */
      readonly kind: "ref";
      readonly path: readonly string[];
      readonly instance?: string;
      readonly source?: SourceSpan;
      readonly instanceSource?: SourceSpan;
    }
  | {
      readonly kind: "string";
      readonly text: string;
      /** v0.9.0 §1.3: the value grammar is uniform, so `"eu" as active` parses —
       *  but an implicit (quoted) place has no scope to name an occupant in, so
       *  validation rejects it (`ASPECT_INSTANCE_ON_STRING`). */
      readonly instance?: string;
      readonly source?: SourceSpan;
      readonly instanceSource?: SourceSpan;
    }
  | { readonly kind: "number"; readonly value: number; readonly source?: SourceSpan }
  | { readonly kind: "boolean"; readonly value: boolean; readonly source?: SourceSpan }
  | { readonly kind: "list"; readonly items: readonly Value[]; readonly source?: SourceSpan }
  | {
      /** §4.3: a getter or render cast in a value slot — `@version`, `@@team`,
       *  `colorize(@@team)`. The SAME expression grammar a view's `style` body
       *  uses, so prose, views and fields read one another's values the same
       *  way. Resolved to a literal per node after cascade (`field-exprs.ts`).
       *
       *  That resolver walks MODULE fields, so a field hanging off a PROCESS
       *  step — a note's `{ latency: … }` body — still carries this kind when
       *  the printer and the evaluators see it. Do not assume it is gone
       *  downstream: dropping it silently printed `latency:` with no value,
       *  which does not re-parse. */
      readonly kind: "expr";
      readonly expr: Expr;
      readonly source?: SourceSpan;
    };

// =============================================================================
// Description
// =============================================================================

/** A bare-string body member. Multiple are concatenated with '\n'. */
export interface DescriptionEntry {
  readonly text: string;
  readonly source?: SourceSpan;
}

// =============================================================================
// Field declaration
// =============================================================================

/**
 * A field assignment or declaration inside a body. Fields are key/value
 * attributes describing the declaring entity.
 *
 * In an instance/instance-subtype body:
 *   - `value` is set, all modifiers are absent
 *
 * In a type body:
 *   - either `value` is set (default) OR `required` is true (mandatory blank)
 *   - `cascade` / `append` may be set independently to specify propagation
 */
export interface FieldDecl {
  /** `@name(args)` lines written above this declaration (see decorations.ts). */
  readonly decorations?: readonly DecorationInstance[];
  readonly path: QualifiedName;
  readonly value?: Value;
  /** Type-body modifier: this field is a mandatory blank. */
  readonly required?: boolean;
  /** Type-body modifier: value cascades to descendants (override mode). */
  readonly cascade?: boolean;
  /** Type-body modifier: value cascades to descendants (compose mode). */
  readonly append?: boolean;
  /**
   * Type-body modifier: this field declares a *cascade group root*
   * (`cascade * widget: …`). Sub-fields nested under this path —
   * whether declared in the same body's `{ … }` block or anywhere
   * else in the cascade chain that shares this group root — are
   * tracked as part of the same group, and replacing the root value
   * downstream drops the whole group from the inherited chain.
   * Only valid alongside `cascade`.
   */
  readonly group?: boolean;
  /**
   * Provenance: this field's value was stamped from the type template
   * (mechanism A), not authored on the instance. Per §4.9 a structural-ancestor
   * cascade value (mechanism B) wins over a template default, so `applyCascade`
   * lets an inherited value override a `fromTemplate` field.
   */
  readonly fromTemplate?: boolean;
  /**
   * Provenance: the expression this field's literal `value` was resolved FROM
   * (`@@team`, `colorize(@@team)`). Set by the post-cascade `field-exprs` pass,
   * which literalises `value` so every consumer keeps reading plain values.
   *
   * Anything writing source text back — WYSIWYG inspector edits, `archlang
   * set` — MUST prefer this over the resolved literal, or editing one node
   * silently freezes a model-wide rule into a constant.
   */
  readonly from?: Expr;
  readonly source?: SourceSpan;
  readonly pathSource?: SourceSpan;
}

// =============================================================================
// Aspect entry
// =============================================================================

/**
 * Entry inside a `aspects { ... }` block, OR a flattened dotted-path aspect
 * declaration like `aspects.security.zone: PCI` at body top level.
 *
 * In a type body:
 *   - either `value` is set OR `required` is true
 *   - `drop` is independent and means "drop this aspect from inherited"
 */
export interface AspectEntry {
  /** `@name(args)` lines written above this declaration (see decorations.ts). */
  readonly decorations?: readonly DecorationInstance[];
  readonly path: QualifiedName;
  readonly value?: Value;
  readonly required?: boolean;
  /** This entry drops an aspect inherited from elsewhere. */
  readonly drop?: boolean;
  readonly source?: SourceSpan;
  readonly pathSource?: SourceSpan;
}

// =============================================================================
// Drop statement (at body top level, not inside aspects block)
// =============================================================================

export interface DropStmt {
  readonly target: QualifiedName;
  readonly source?: SourceSpan;
  readonly targetSource?: SourceSpan;
}

// =============================================================================
// Sub-declarations: surfaces, interfaces, sub-modules
// =============================================================================

export interface SurfaceDecl {
  /** `@name(args)` lines written above this declaration (see decorations.ts). */
  readonly decorations?: readonly DecorationInstance[];
  readonly typeName: Name;
  readonly name: Name;
  /** True when the name was omitted and derived from the type (§4.14). Only
   *  modules may be unnamed; an unnamed surface is a validation error. */
  readonly unnamed?: boolean;
  /** Braces were present at the declaration. Recorded on every decl so text
   *  synthesizers can materialize a body instead of brace-scanning past a
   *  bodyless target. */
  readonly hasBody?: boolean;
  readonly body: Body;
  /** Type-body marker: instances/subtypes must fulfill or drop. */
  readonly required?: boolean;
  /** Instance/subtype-body marker: this declaration overrides an inherited one. */
  readonly override?: boolean;
  readonly source?: SourceSpan;
  readonly typeSource?: SourceSpan;
  readonly nameSource?: SourceSpan;
}

export interface InterfaceDecl {
  /** `@name(args)` lines written above this declaration (see decorations.ts). */
  readonly decorations?: readonly DecorationInstance[];
  readonly typeName: Name;
  readonly name: Name;
  /** True when the name was omitted and derived from the type (§4.14). An
   *  unnamed interface is a validation error UNLESS it is braced — a braced
   *  unnamed interface is a module's default interaction point (§1.6). */
  readonly unnamed?: boolean;
  /** v0.9.0 §1.6: braces were present at the declaration (`interface {}` vs a
   *  bare `interface`). Distinguishes a default interface from an unnamed
   *  interface that merely lacks a name — the two produce identical AST
   *  otherwise. Recorded on every decl (named included) so text synthesizers
   *  can materialize a body instead of brace-scanning past a bodyless target. */
  readonly hasBody?: boolean;
  readonly body: InterfaceBody;
  readonly required?: boolean;
  readonly override?: boolean;
  /** True if prefixed with `export` — published across the space boundary (v0.5). */
  readonly exported?: boolean;
  readonly source?: SourceSpan;
  /** Full prefixed extent when a prefix clause precedes the decl (`export`):
   *  `source` deliberately excludes prefixes; delete/move consume this. */
  readonly extentSource?: SourceSpan;
  readonly typeSource?: SourceSpan;
  readonly nameSource?: SourceSpan;
}

export interface ModuleDecl {
  /** `@name(args)` lines written above this declaration (see decorations.ts). */
  readonly decorations?: readonly DecorationInstance[];
  readonly typeName: Name;
  readonly id?: StableId;
  readonly name: Name;
  /** v0.9.0 §1.2: born-on planes from an `on <planes>` prefix (declaration
   *  context) — replaces primary. Undefined = primary only. */
  readonly onPlanes?: readonly QualifiedName[];
  /**
   * True when the name was omitted and derived from the type
   * (snake_case → UpperCamelCase, §4.14). Custom types only, single-use
   * per parent, no `#id` until promoted. Not a TODO.
   */
  readonly unnamed?: boolean;
  /** v0.9.0 §1.6: braces were present at the declaration. Carried so the
   *  resolver can flag a reclassified user-interface-kind default
   *  (`kafka {}` where `kafka` is a user interface kind parses as a module),
   *  and — recorded on every decl (named included) — so text synthesizers can
   *  materialize a body instead of brace-scanning past a bodyless target. */
  readonly hasBody?: boolean;
  /** Parsed from `<kind> <name> in <qualified-parent>`. */
  readonly parentPath?: QualifiedName;
  readonly body: Body;
  readonly required?: boolean;
  readonly override?: boolean;
  /** True if prefixed with `export` — sanctions being named from another space
   *  (v0.5 §4.11). On a module that means being used as a PLACE: an aspect
   *  membership pointing here from another space is blessed rather than flagged
   *  (§1.3). Interface-level coupling is still governed by the interface's own
   *  `export`. */
  readonly exported?: boolean;
  readonly source?: SourceSpan;
  /** Full prefixed extent when a prefix clause precedes the decl (`in <Parent>`
   *  / single-form `on <planes>`): `source` deliberately excludes prefixes
   *  (incremental slicing depends on that); delete/move consume this instead.
   *  Absent for block-form `on p { … }` members — the header is shared. */
  readonly extentSource?: SourceSpan;
  readonly typeSource?: SourceSpan;
  readonly idSource?: SourceSpan;
  readonly nameSource?: SourceSpan;
  readonly parentSource?: SourceSpan;
}

// =============================================================================
// Bodies
// =============================================================================

/**
 * A module / surface / type body. Order is not significant; the parser
 * collects each member type into its own array. Resolution merges
 * across the type chain and applies cascade/structural rules.
 */
/**
 * v0.9.0 §1.4: a place-side membership roster — `aspect <key> for: A as x, B`
 * or the block twin. Each entry desugars to the member's own
 * `aspect <key>: <this place> as <instance>`.
 */
export interface AspectRoster {
  readonly key: QualifiedName;
  readonly entries: readonly {
    readonly path: QualifiedName;
    readonly instance?: string;
    readonly source?: SourceSpan;
    readonly instanceSource?: SourceSpan;
  }[];
  readonly source?: SourceSpan;
  readonly keySource?: SourceSpan;
}

/**
 * v0.9.0 §1.2: partial presence — `on <plane> { members }` in a module body.
 * Declarations inside are plane-exclusive members; bare names are references
 * to main-body members (shared). The resolver classifies (a bare single
 * segment that is a registered type parses as an unnamed decl here and is
 * reclassified when appropriate).
 */
export interface PartialPresence {
  readonly plane: QualifiedName;
  readonly body: Body;
  /** Bare (possibly dotted) names written in the block — each references a
   *  member of the MAIN body, which then rides to the plane while staying on
   *  primary. A reference that resolves to nothing is a validation error: a
   *  presence block projects existing members, it never auto-stubs. */
  readonly refs?: readonly PresenceRef[];
  readonly source?: SourceSpan;
  readonly planeSource?: SourceSpan;
}

/** One member reference inside a partial-presence block (§1.2). */
export interface PresenceRef {
  readonly path: QualifiedName;
  readonly source?: SourceSpan;
}

/** v0.9.0 §1.2: a bare reference inside a top-level `on <plane> { … }` block —
 *  adds the plane(s) to an existing module's plane set. */
export interface OnInclusion {
  readonly planes: readonly QualifiedName[];
  readonly target: QualifiedName;
  readonly source?: SourceSpan;
}

export interface Body {
  readonly fields: readonly FieldDecl[];
  readonly aspects: readonly AspectEntry[];
  /** v0.9.0 §1.4: `aspect <key> for` rosters declared on this body. */
  readonly rosters?: readonly AspectRoster[];
  /** v0.9.0 §1.2: body `on p1, p2` — ALSO-ON planes (additive to primary). */
  readonly alsoOn?: readonly QualifiedName[];
  /** v0.9.0 §1.2: body `on p { … }` partial-presence blocks. */
  readonly partialOn?: readonly PartialPresence[];
  readonly descriptions: readonly DescriptionEntry[];
  readonly drops: readonly DropStmt[];
  readonly surfaces: readonly SurfaceDecl[];
  readonly interfaces: readonly InterfaceDecl[];
  readonly modules: readonly ModuleDecl[];
  /**
   * Module-scoped processes declared lexically inside this body.
   * The resolver hoists these to the model's flat `processes` array
   * and stamps `ownerPath` on each so their qualified name becomes
   * `<owner-path>.<name>`. Empty for surface/interface bodies.
   */
  readonly processes: readonly ProcessDecl[];
  /**
   * Module- or type-scoped reusable subprocesses. When declared on a
   * `type module …` body, every instance inherits the subprocess via
   * template stamping; processes owned by the instance (or any
   * descendant) may invoke them with `do X(...)`. When declared
   * directly on an instance body, scoping is the same — visible to
   * processes owned by that module and its descendants.
   */
  readonly subprocesses: readonly SubprocessDecl[];
  /**
   * v0.10 §5.2: module-scoped view declarations. Hoisted to the model's flat
   * `views` array with `ownerPath` stamped (the module-scoped-process
   * precedent); a scoped view resolves selector names from its module context.
   */
  readonly views?: readonly ViewDeclV2[];
  /**
   * v0.10 §4/§5.2: module-scoped policies. Scope = the owning module's
   * SUBTREE — subjects are the selector intersected with the scope's elements,
   * edges by their source endpoint. Collected by `collectScopedPolicies`.
   */
  readonly policies?: readonly PolicyDecl[];
  /** v0.10 §1.9/§5.2: module-scoped query vocabulary. */
  readonly queries?: readonly QueryDecl[];
  /** Process-tests P0: module-scoped `test` declarations (INERT until eval). */
  readonly tests?: readonly TestDecl[];
  /** v0.10 §3/§5.2: module-level `lens { … }` blocks (inspector-wide in scope). */
  readonly lenses?: readonly LensDecl[];
  /**
   * Tombstones for the structural cascade (§4.9 "drop is absolute"). When a
   * body drops a cascade field or an aspect, the dropped path is recorded here so
   * `applyCascade` (mechanism B) stops re-inheriting it from a structural
   * ancestor for this module AND its descendants — the cascade chain is broken
   * at the drop point. Entity drops (sub-modules/surfaces/interfaces) do not
   * cascade and are not recorded. Each entry carries `matchedLocally` =
   * whether the drop matched a field/aspect present in this body at merge time
   * (used to validate genuinely-uninherited drops against the cascade chain).
   */
  readonly cascadeDrops?: readonly CascadeDrop[];
}

/** A field/aspect drop recorded for structural-cascade resolution (§4.9). */
export interface CascadeDrop {
  readonly target: QualifiedName;
  readonly kind: "field" | "aspect";
  /** True when the drop removed a field/aspect present in this body at merge time. */
  readonly matchedLocally: boolean;
  readonly source?: SourceSpan;
}

/** Empty body singleton used when a declaration has no body block. */
export const EMPTY_BODY: Body = {
  fields: [],
  aspects: [],
  descriptions: [],
  drops: [],
  surfaces: [],
  interfaces: [],
  modules: [],
  processes: [],
  subprocesses: [],
};

/**
 * Interface body — leaves only carry attributes (fields, aspects,
 * descriptions). Interfaces are leaves: no nested declarations.
 */
export interface InterfaceBody {
  readonly fields: readonly FieldDecl[];
  readonly aspects: readonly AspectEntry[];
  readonly descriptions: readonly DescriptionEntry[];
  readonly drops: readonly DropStmt[];
}

export const EMPTY_INTERFACE_BODY: InterfaceBody = {
  fields: [],
  aspects: [],
  descriptions: [],
  drops: [],
};

// =============================================================================
// Top-level: type declaration
// =============================================================================

/**
 * A type declaration.
 *
 * `type [#id] <parentType> <name> { body }`
 *
 * `parentType` is resolved via the kind registry. Every type's parent
 * eventually roots at one of the base kinds (`module`, `surface`,
 * `interface`). The base kinds `process` and `view` are not valid
 * parent kinds — process/view subtyping is reserved for future iterations.
 */
export interface TypeDecl {
  /** `@name(args)` lines written above this declaration. On a type HEADER these
   *  annotate the type itself; a decoration inside the type BODY is stamped
   *  onto instances as a form-template default (§4.1). */
  readonly decorations?: readonly DecorationInstance[];
  readonly id?: StableId;
  readonly parentType: Name;
  readonly name: Name;
  readonly body: Body;
  /** True if the declaration was prefixed with `export`. Drives wildcard imports. */
  readonly exported?: boolean;
  readonly source?: SourceSpan;
  readonly idSource?: SourceSpan;
  readonly parentTypeSource?: SourceSpan;
  readonly nameSource?: SourceSpan;
  /**
   * The dotted package name this type originates from. Set by
   * `resolvePackage` after merging imports: locally declared types
   * are tagged with the current package's name; synthesized imports
   * are tagged with their source package. Undefined for anonymous
   * packages (no manifest).
   */
  readonly library?: string;
}

// =============================================================================
// Top-level: process declaration
// =============================================================================

export interface ProcessDecl {
  /** `@name(args)` lines written above this declaration (see decorations.ts). */
  readonly decorations?: readonly DecorationInstance[];
  readonly id?: StableId;
  /** v0.9.0 §1.2: born-on planes from an `on <planes>` prefix (declaration
   *  context) — replaces primary. Undefined = primary only. */
  readonly onPlanes?: readonly QualifiedName[];
  readonly name: Name;
  /**
   * True when this process has no author-given name — it wraps one or
   * more bare steps written directly in a declaration position (§4.13).
   * Anonymous processes carry an empty `name`, never an `id`, and are a
   * compiler TODO until named. They are skipped by name-keyed indexes.
   */
  readonly anonymous?: boolean;
  readonly steps: readonly ProcessStep[];
  readonly description?: string;
  /** True if prefixed with `export` — a sanctioned cross-space `do` entry point
   *  (§4.11, v0.10.1). Legal on TOP-LEVEL process declarations only; the parser
   *  rejects `export` on a module-body or `in`-placed process. */
  readonly exported?: boolean;
  /** v0.9.0 §2.7: header-zone fields (before the first action). */
  readonly fields?: readonly FieldDecl[];
  /** Fields written AFTER the first action — parseable for recovery; the
   *  validator rejects each (`PROCESS_FIELD_AFTER_ACTION`). */
  readonly lateFields?: readonly FieldDecl[];
  /** v0.10 §5.1: header-zone classification aspects (before the first action) —
   *  e.g. `aspect team: "Ops"`. Cascade-override from the owning module (a
   *  module-scoped process inherits the module's aspects; its own declaration
   *  overrides), resolved in `applyCascade`. Own-declared entries only here. */
  readonly aspects?: readonly AspectEntry[];
  readonly source?: SourceSpan;
  /** Full prefixed extent (`in <Parent>` / single-form `on <planes>`); see
   *  ModuleDecl.extentSource. */
  readonly extentSource?: SourceSpan;
  readonly idSource?: SourceSpan;
  readonly nameSource?: SourceSpan;
  /** Optional subprocesses declared lexically inside this process. */
  readonly subprocesses?: readonly SubprocessDecl[];
  /**
   * Optional `in <ModulePath>` clause on a top-level declaration.
   * The resolver hoists the process under the named module, merging it
   * with body-nested processes there. Mirrors `ModuleDecl.parentPath`.
   *
   * MUST be undefined on processes declared inside a module body — the
   * lexical placement already names the owner. Parser rejects the combo.
   */
  readonly parentPath?: QualifiedName;
  readonly parentSource?: SourceSpan;
  /**
   * Set by the resolver after hoisting body-nested or `in`-attached
   * processes. Reflects the qualified name of the owning module
   * (e.g. ["Platform","Orders"]). Undefined for top-level orphans.
   * The fully-qualified process name is `[...ownerPath, name].join(".")`.
   */
  readonly ownerPath?: QualifiedName;
  /** Set by the resolver when this decl was written `in <Target>` but the
   *  target failed to resolve (unknown or ambiguous). The decl is emitted
   *  unscoped so the model stays renderable, but it never entered the unscoped
   *  scope — so it is excluded from the unscoped uniqueness bucket (the failed
   *  placement already carries its own diagnostic). */
  readonly placementFailed?: true;
  /**
   * v0.11 (space-scoped naming, D1a): the process's LANDED identity space —
   * the owning module's space for module-scoped processes (body-nested or
   * `in`-placed), the declaring file's space for free-standing ones.
   * Resolver-stamped like `ownerPath`. Undefined at package root.
   */
  readonly space?: QualifiedName;
}

/**
 * Subprocess — a reusable named flow. Top-level subprocesses are
 * visible across the package; subprocesses nested inside a `process`
 * are scoped to that process only. Subprocesses are NOT rendered as
 * standalone diagrams; they only appear when invoked via `do X(...)`.
 */
export interface SubprocessDecl {
  /** `@name(args)` lines written above this declaration (see decorations.ts). */
  readonly decorations?: readonly DecorationInstance[];
  readonly id?: StableId;
  /** v0.9.0 §1.2: born-on planes from an `on <planes>` prefix (declaration
   *  context) — replaces primary. Undefined = primary only. */
  readonly onPlanes?: readonly QualifiedName[];
  readonly name: Name;
  /**
   * `on Caller` — the template default-caller parameter (Spec §5.5). Bound at
   * each `do` site by the `do`'s optional caller prefix and substituted into
   * this subprocess's owner-omitted steps. Undefined when no `on` clause.
   */
  readonly onCaller?: string;
  readonly onCallerSource?: SourceSpan;
  readonly params: readonly string[];
  readonly steps: readonly ProcessStep[];
  readonly description?: string;
  /** v0.9.0 §2.7: header-zone fields / late (rejected) fields — see ProcessDecl. */
  readonly fields?: readonly FieldDecl[];
  readonly lateFields?: readonly FieldDecl[];
  /** True if prefixed with `export` — a sanctioned cross-space entry point (v0.5). */
  readonly exported?: boolean;
  readonly source?: SourceSpan;
  /** Full prefixed extent (`export` / single-form `on <planes>`); see
   *  ModuleDecl.extentSource. */
  readonly extentSource?: SourceSpan;
  readonly idSource?: SourceSpan;
  readonly nameSource?: SourceSpan;
  readonly subprocesses?: readonly SubprocessDecl[];
  /**
   * Qualified path scoping this subprocess. Filled by the resolver.
   *
   *   - For a subprocess declared inside a `process` body, this is the
   *     owning process's fully-qualified name (`Owner.Process` or just
   *     `Process` for free-standing processes). The subprocess is
   *     callable only from inside that process.
   *   - For a subprocess declared inside a module/type body, this is
   *     the owning module's qualified path. Inheritable to instances;
   *     `do X(...)` from any process owned by that module (or a
   *     descendant) resolves to it.
   *   - Undefined for free-standing top-level subprocesses (callable
   *     from any process).
   */
  readonly ownerPath?: QualifiedName;
  /** v0.11 (D1a): LANDED identity space — the owning module's/process's
   *  space, or the declaring file's for free-standing subprocesses.
   *  Resolver-stamped like `ownerPath`. */
  readonly space?: QualifiedName;
}

/**
 * v0.9.0 §2.6: free-standing flow prose — a bare string AFTER the first
 * action of a process body (or between two steps of a bare run). Rendered at
 * its position; not an action, no owner, no edge, transparent to `|` ditto.
 */
export interface ProseStep {
  readonly kind: "prose";
  readonly text: string;
  readonly span?: SourceSpan;
}

export type ProcessStep =
  | SimpleStep
  | NoteStep
  | ProseStep
  | EachStep
  | IfStep
  | SelectStep
  | TryStep
  | ParallelStep
  | ReversibleStep
  | AwaitStep
  | DoStep
  | GoStep
  | OwnerBlockStep
  | FailStep
  | FinishStep;

/**
 * The syntactic owner prefix on an action node (Spec §5.5, PROCESS.md §2). The
 * OWNER is who performs the node. Three written forms; an **absent** owner field
 * means "none" (omitted — resolves to the nearest enclosing owner at a later
 * phase, else a TODO). Direction (inbound vs outbound) is a SEMANTIC property
 * derived in the IR, never a syntactic distinction, so it is NOT recorded here.
 *
 *  - `explicit` — a written qualified name (`Orders if …`); `path` is set.
 *  - `ditto`    — `|` : the previous sibling step's HEAD (§4.13.4).
 *  - `snake`    — `\` : the previous sibling step's TAIL (§4.13.4). Call-only,
 *                 so it only ever reaches a call head.
 *  - `dist`     — `dist` : emergent / choreographed, no coordinator
 *                 (control-only; a call always has a concrete caller).
 */
export interface StepOwner {
  readonly form: "explicit" | "ditto" | "snake" | "dist";
  /** The written performer path — present only for `form: "explicit"`. */
  readonly path?: QualifiedName;
  readonly source?: SourceSpan;
}

/**
 * A free-form action step anchored on an owner: `Actor "the action text"`.
 *
 * v0.8: the owner is REQUIRED — a bare string with no owner is a *description*,
 * not a note (the parser routes it to {@link ProcessDecl.description}). The
 * caller (= owner) may be a qualified name or `|` (ditto, {@link actorDitto});
 * it is never `dist`.
 *
 * The text comes from a regular `"…"` or triple-quoted `"""…"""`
 * string literal; lexer normalization (autotrim + common-indent
 * dedent) is already applied by the time it reaches here.
 */
export interface NoteStep {
  readonly kind: "note";
  /** Lane anchor (= owner). Absent only on malformed input; `|` ditto is
   *  marked by {@link actorDitto} with an empty `actor`. */
  readonly actor?: QualifiedName;
  /** True when the owner was written as `|` (ditto = previous sibling's owner). */
  readonly actorDitto?: boolean;
  readonly text: string;
  /** Optional `{ latency: … }` field body (latency analysis, PROCESS.md
   *  "Time analysis"). Fields only — no sub-declarations. Prices the note as
   *  business time; unannotated notes cost 0. */
  readonly fields?: readonly FieldDecl[];
  readonly source?: SourceSpan;
  readonly actorSource?: SourceSpan;
}

/**
 * `caller > callee(arg1, arg2)` with an optional `bind = ` prefix and a
 * trailing `as name`.
 *
 * `args` and `bind` are freeform diagram annotations — they are NOT
 * validated against any interface signature or scope. Only `from` and
 * `to` are resolved. The CALLER (left of `>`) is the step's owner.
 */
export interface SimpleStep {
  readonly kind: "simple";
  /** `@name(args)` lines above this step. Plain-metadata decorations only —
   *  lifecycle is forbidden on a step (a step is model behaviour, not a
   *  lifecycle-bearing node), and a step decoration is INERT: it does not alter
   *  the walk, the derived edges, or a process test's trace. */
  readonly decorations?: readonly DecorationInstance[];
  /**
   * Caller — qualified name resolving to a module, actor, or bound var.
   *
   * Always the caller **as written** — never resolved here. Empty (`[]`)
   * marks an **anonymous head**: a step written with no explicit caller
   * (`> Callee`), which the IR builder resolves to the enclosing owner
   * (§4.13.1); an empty head that survives to a free-standing (owner-less)
   * process is the error `PROCESS_ANON_HEAD_AT_ROOT`. A `|` fan caller is
   * marked by {@link fromDitto} and a `\` snake caller by {@link fromSnake}
   * (both also with an empty `from`).
   */
  readonly from: QualifiedName;
  /** True when the caller was written as `|` (fan = the previous sibling
   *  step's HEAD, §4.13.4). Distinct from an anonymous head. */
  readonly fromDitto?: boolean;
  /** True when the caller was written as `\` (snake = the previous sibling
   *  step's TAIL, §4.13.4) — the same run, one hop further. Call-only, and
   *  never inherited from a container: with no tail to continue, the caller
   *  stays empty and the compiler raises `SNAKE_NO_TAIL`. */
  readonly fromSnake?: boolean;
  /** Callee — qualified name resolving to a module's interface. */
  readonly to: QualifiedName;
  /**
   * Marks the callee interface as **unspecified** (§4.13.2): the step
   * was written with a trailing dot (`> Payments.`) or its callee
   * resolves to a module, so a synthesized anonymous interface (a TODO)
   * stands in for the missing leaf.
   */
  readonly toUnspecified?: boolean;
  /** Optional `name =` binding the call's result for downstream reference. */
  readonly bind?: string;
  /** Optional `as name` — names the step for `go` / diff (§7). On a chain it
   *  attaches to the final hop. */
  readonly asName?: string;
  /** Freeform call arguments. Not validated. */
  readonly args?: readonly string[];
  /** Optional trailing freehand comment (`> Callee "note"`, §5.5 / PROCESS.md §1)
   *  — rendered on the step's edge + sidebar, never validated. On a chain it
   *  attaches to the final hop. */
  readonly comment?: string;
  readonly source?: SourceSpan;
  readonly fromSource?: SourceSpan;
  readonly toSource?: SourceSpan;
  readonly bindSource?: SourceSpan;
  readonly asNameSource?: SourceSpan;
  readonly commentSource?: SourceSpan;
}

/**
 * `each [label] [in source] (body | retry-tail)` or `: oneliner`.
 *
 * Label and source are freeform — purely diagram annotations on the
 * loop bracket. No iteration validation. When {@link retry} is present the
 * loop is the **retry form** (`each <bound> try { } catch { } else { }`,
 * PROCESS.md §4) and {@link steps} is empty; otherwise {@link steps} is the
 * plain loop body.
 */
export interface EachStep {
  readonly kind: "each";
  readonly owner?: StepOwner;
  readonly label?: string;
  readonly source?: string;
  readonly steps: readonly ProcessStep[];
  /** Present for the retry form `each <bound> try/catch/else`. */
  readonly retry?: RetryTail;
  readonly span?: SourceSpan;
}

/**
 * The `try { } catch <label> { } else { }` tail of a retry loop. Distinct from a
 * plain {@link TryStep} nested in a loop body (a per-iteration guard): a retry
 * exits on success and runs `else` when the bound is exhausted.
 */
export interface RetryTail {
  readonly attempt: readonly ProcessStep[];
  readonly catches: readonly CatchClause[];
  /** `else { … }` — bound exhausted without success. */
  readonly elseSteps?: readonly ProcessStep[];
  readonly span?: SourceSpan;
}

/**
 * `if [cond] [body] else if [cond] [body] else [body]`.
 *
 * `cond` is a freeform identifier or quoted string — diagram label
 * only, never evaluated. v0.8: both the condition and the body of any arm
 * are OPTIONAL (a condition with no body is a draft branch).
 */
export interface IfStep {
  readonly kind: "if";
  readonly owner?: StepOwner;
  readonly branches: readonly IfBranch[];
  /** Present when a trailing `else` arm was written. Its `steps` may be
   *  undefined (a body-less `else`). */
  readonly elseBranch?: IfBranch;
  /**
   * Steps of the trailing `else` arm. Convenience mirror of
   * `elseBranch?.steps`; undefined when there is no `else` or it is body-less.
   */
  readonly elseSteps?: readonly ProcessStep[];
  readonly span?: SourceSpan;
}

export interface IfBranch {
  /** Free-form condition; undefined for the `else` arm or a body-only `if`. */
  readonly condition?: string;
  /** Span of the condition token (identifier bare / string with quotes) — drives
   *  inline-edit of the branch condition. Absent when `condition` is. */
  readonly conditionSource?: SourceSpan;
  /** Undefined = no body written (a body-less draft branch). */
  readonly steps?: readonly ProcessStep[];
  readonly span?: SourceSpan;
}

/**
 * `select [count] [head] { Label body … }` — the inclusive multi-way choice
 * (v0.8, replaces the v0.7 `switch`). Every matching case runs; `select one`
 * runs the first matching, `select N` the first N matching, bare `select` all.
 *
 * Head and case aspects are freeform identifiers or quoted strings.
 * No exhaustiveness check; no module-ref interpretation of aspects.
 */
export interface SelectStep {
  readonly kind: "select";
  readonly owner?: StepOwner;
  /** `one` / `N`; undefined = bare `select` (all matching). */
  readonly count?: SelectCount;
  readonly head?: string;
  /** Span of the head token (identifier bare / string with quotes) — drives
   *  inline-edit of the select head. Absent when `head` is. */
  readonly headSource?: SourceSpan;
  readonly cases: readonly SelectCase[];
  readonly span?: SourceSpan;
}

/** `one` (= first matching) or `n` (= first N matching). Absent = all. */
export type SelectCount =
  | { readonly kind: "one" }
  | { readonly kind: "n"; readonly n: number; readonly source?: SourceSpan };

export interface SelectCase {
  readonly label: string;
  /** Span of the case-label token (identifier bare / string with quotes) —
   *  drives inline-edit of the case label. */
  readonly labelSource?: SourceSpan;
  readonly steps: readonly ProcessStep[];
  readonly span?: SourceSpan;
}

/**
 * `try { } catch [Label] { } catch [Label] { }` — the plain guard (handle the
 * failure, continue forward; no retry).
 *
 * Catch aspects are freeform identifiers or quoted strings. Multiple
 * catches allowed; each renders as its own error swimlane.
 */
export interface TryStep {
  readonly kind: "try";
  readonly owner?: StepOwner;
  readonly trySteps: readonly ProcessStep[];
  readonly catches: readonly CatchClause[];
  readonly span?: SourceSpan;
}

export interface CatchClause {
  readonly label?: string;
  readonly steps: readonly ProcessStep[];
  readonly span?: SourceSpan;
}

/**
 * `parallel [out] [join|race [count|bool]] <body>` (PROCESS.md §5). The prefix
 * {@link MergeClause} sets how branches re-converge; the body takes one of three
 * forms:
 *   - plain named branches (`{ branch … }`)            → {@link branches}
 *   - `select [count] { branch … }` (concurrent incl.) → {@link select} + {@link branches}
 *   - `each <head> { … }` fan-out                       → {@link fanOut}
 */
export interface ParallelStep {
  readonly kind: "parallel";
  readonly owner?: StepOwner;
  /** Prefix merge clause; undefined for a bare `parallel { … }` (AND-join). */
  readonly merge?: MergeClause;
  /** True for the `parallel select … { branch … }` concurrent-inclusive form. */
  readonly select?: boolean;
  /** Count on `parallel select N`; undefined = all (with {@link select}). */
  readonly selectCount?: SelectCount;
  /** Named branches — the plain and `select` forms. Empty for the fan-out form. */
  readonly branches: readonly ParallelBranch[];
  /** Present for the `each <head> { … }` fan-out form. */
  readonly fanOut?: ParallelFanOut;
  /**
   * v0.9.0 §2.2: `parallel await <head> { W } else { E }` boundary clause.
   * Implies race semantics; the body is a single implicit branch. `elseSteps`
   * is the timeout branch (absent → the walker synthesizes `fail "<head>"`).
   */
  readonly awaitHead?: string;
  readonly awaitHeadSource?: SourceSpan;
  /** Optional `as <label>` alias on the await boundary (`parallel await 1 day as
   *  grace`) — a stable key a process-test `elapses`/`within` pin resolves to. */
  readonly awaitAs?: string;
  readonly awaitAsSource?: SourceSpan;
  readonly elseSteps?: readonly ProcessStep[];
  readonly span?: SourceSpan;
}

/**
 * One `branch [name] (: step | { block } | control)` of a parallel. The name
 * (when present) is what a boolean merge refers to.
 */
export interface ParallelBranch {
  readonly name?: string;
  readonly nameSource?: SourceSpan;
  readonly steps: readonly ProcessStep[];
  /** v0.9.0 §2.3: the single implicit branch of a body written without the
   *  `branch` keyword (`parallel out { … }`) — printers omit the keyword. */
  readonly implicit?: boolean;
  readonly span?: SourceSpan;
}

/** The `each <head> { … }` fan-out body of a `parallel`. */
export interface ParallelFanOut {
  readonly label?: string;
  readonly source?: string;
  readonly steps: readonly ProcessStep[];
  readonly span?: SourceSpan;
}

/**
 * The prefix merge clause on a `parallel`. `out` detaches (no wait); `join`
 * proceeds at the threshold and keeps losers running; `race` proceeds at the
 * threshold and cancels losers. The threshold is a {@link count} or a
 * {@link bool} over branch names. `out` may stack with `join`/`race`.
 */
export interface MergeClause {
  /** `out` modifier — detach, do not wait. */
  readonly out?: boolean;
  /** `join` keeps losers; `race` cancels them. Undefined when only `out`. */
  readonly policy?: "join" | "race";
  /** `join N` / `race N` threshold. */
  readonly count?: number;
  readonly countSource?: SourceSpan;
  /** Boolean over branch names (`(fraud and credit) or override`). */
  readonly bool?: MergeBool;
  readonly source?: SourceSpan;
}

/** Boolean over parallel branch names; `and`/`or` only, no `not`. */
export type MergeBool =
  | { readonly kind: "name"; readonly name: string; readonly source?: SourceSpan }
  | { readonly kind: "and"; readonly terms: readonly MergeBool[] }
  | { readonly kind: "or"; readonly terms: readonly MergeBool[] };

/**
 * `reversible [on …] { … }` — a saga span (PROCESS.md §8). The trigger defaults
 * to `on error`. Each forward item may carry an `unwind` (inline
 * catch-and-rethrow); an item may also be reversal-only.
 */
export interface ReversibleStep {
  readonly kind: "reversible";
  readonly owner?: StepOwner;
  /** The `on <text>` trigger annotation; undefined = the defaulted `on error`. */
  readonly trigger?: string;
  readonly triggerSource?: SourceSpan;
  readonly items: readonly ReversibleItem[];
  readonly span?: SourceSpan;
}

/**
 * One item of a `reversible` block: an optional forward action (a process step
 * or a bare grouping {@link BlockStep}) paired with an optional `unwind` and an
 * optional `as name`. A reversal-only line leaves {@link forward} undefined.
 */
export interface ReversibleItem {
  readonly forward?: ProcessStep | BlockStep;
  readonly unwind?: UnwindClause;
  readonly asName?: string;
  readonly asNameSource?: SourceSpan;
  readonly span?: SourceSpan;
}

/**
 * `unwind <action>` — the reverse action paired with a forward step/block. The
 * action is a **complete statement**: a call (with a resolvable caller), a
 * terminator, a `do`, or a block. A bare interface with no `>` is invalid (the
 * caller is unknown) and the parser flags it.
 */
export interface UnwindClause {
  readonly action: SimpleStep | FailStep | FinishStep | DoStep | BlockStep;
  readonly source?: SourceSpan;
}

/**
 * A bare `{ … }` grouping with no owner prefix. Appears only as a `reversible`
 * forward item or an `unwind` action (so one `unwind` can cover a run of steps);
 * NOT a member of the {@link ProcessStep} union.
 */
export interface BlockStep {
  readonly kind: "block";
  readonly steps: readonly ProcessStep[];
  readonly span?: SourceSpan;
  /** Set when the parser SYNTHESIZED this block to hold the hops of one chained
   *  line (a `reversible` forward or an `unwind` action), so the item stays a
   *  single node. Such a wrapper is not a body: the glyphs read through it to
   *  the chain's head/tail (§4.13.4). An AUTHORED `{ }` has no flag and is a
   *  body boundary — its steps are not siblings of what follows. */
  readonly chain?: boolean;
}

/**
 * `await <free-form>` — a timed / event wait owned by the waiter
 * (`Booking await 1 day`, `await "until ready"`). The target is rendered, not
 * resolved.
 */
export interface AwaitStep {
  readonly kind: "await";
  readonly owner?: StepOwner;
  readonly target: string;
  readonly targetSource?: SourceSpan;
  readonly span?: SourceSpan;
}

/**
 * `do [Caller] X(args)` — invoke a subprocess. Target is a (potentially
 * qualified) reference; args are freeform names. `do` is an ownerless structural
 * splice; an optional caller prefix ({@link templateCaller}) is the TEMPLATE
 * default-caller bound to `subprocess X on Caller`, not an owner of the `do`.
 * Resolution is deferred to validation.
 */
export interface DoStep {
  readonly kind: "do";
  readonly target: QualifiedName;
  /** Template default-caller (`Caller do X` / `| do X`); never `dist`. */
  readonly templateCaller?: StepOwner;
  readonly args?: readonly string[];
  /** Optional `as` label (`do X as pay1`, §7) — names the splice so a test can
   *  namespace the choices/steps inside it (design §6, do-splice identity). */
  readonly asName?: string;
  readonly asNameSource?: SourceSpan;
  /** Optional trailing freehand comment (`do X "note"`, §5.5). Sidebar only —
   *  a splice yields no single edge. */
  readonly comment?: string;
  readonly span?: SourceSpan;
  readonly targetSource?: SourceSpan;
  readonly commentSource?: SourceSpan;
}

/**
 * `go <name>` — re-enter a step named with `as` and resume forward (PROCESS.md
 * §7). Ownerless structural op; the target is resolved at a later phase.
 */
export interface GoStep {
  readonly kind: "go";
  readonly target: string;
  /** Optional trailing freehand comment (`go name "note"`, §5.5) — rendered on
   *  the re-entry aux arc + sidebar. */
  readonly comment?: string;
  readonly targetSource?: SourceSpan;
  readonly span?: SourceSpan;
  readonly commentSource?: SourceSpan;
}

/**
 * `Owner { … }` (or `| { … }` / `dist { … }`) — set the owner (which may be
 * `dist`) for a run of steps. The owner becomes the nearest enclosing owner for
 * the block's children.
 */
export interface OwnerBlockStep {
  readonly kind: "owner_block";
  readonly owner: StepOwner;
  readonly steps: readonly ProcessStep[];
  readonly span?: SourceSpan;
}

/** `fail [all] [string]` — explicit error terminator with optional reason.
 *  `all` (v0.9.0 §2.4, same-line contextual) ends every live branch of the
 *  enclosing process, not just the current branch. */
export interface FailStep {
  readonly kind: "fail";
  readonly all?: boolean;
  readonly reason?: string;
  /** Span of the quoted reason STRING token (quotes included), for inline edit
   *  of the reason without a whole-node rewrite. Absent when no reason written. */
  readonly reasonSource?: SourceSpan;
  readonly span?: SourceSpan;
}

/** `finish [all] [string]` — explicit success terminator with optional label.
 *  `all` = BPMN-terminate: stop every live branch (no compensation). */
export interface FinishStep {
  readonly kind: "finish";
  readonly all?: boolean;
  readonly label?: string;
  /** Span of the quoted label STRING token (quotes included) — see
   *  {@link FailStep.reasonSource}. */
  readonly labelSource?: SourceSpan;
  readonly span?: SourceSpan;
}

// =============================================================================
// Top-level: view declaration
// =============================================================================

// The v0.10 view (ordered selector clauses) is `ViewDeclV2` in v010-ast.ts.

// =============================================================================
// Top-level discriminated union & file
// =============================================================================

export type TopLevelDecl =
  | { readonly kind: "type"; readonly decl: TypeDecl }
  | { readonly kind: "module"; readonly decl: ModuleDecl }
  | { readonly kind: "process"; readonly decl: ProcessDecl }
  | { readonly kind: "subprocess"; readonly decl: SubprocessDecl }
  | { readonly kind: "view"; readonly decl: ViewDeclV2 }
  | { readonly kind: "extension"; readonly decl: ExtensionDecl }
  // v0.10 governance/query surface (§1.9, §4). Parsed here; INERT until the P1b
  // evaluator lands — the resolver collects and ignores them (the same "not yet
  // evaluated" posture as `when`/`use policy`). Types live in v010-ast.ts.
  | { readonly kind: "query"; readonly decl: QueryDecl }
  | { readonly kind: "policy"; readonly decl: PolicyDecl }
  // Process-tests P0 (grammar only): parsed and carried INERT until the
  // trace-set evaluator lands, the same posture as `policy`/`query`.
  | { readonly kind: "test"; readonly decl: TestDecl }
  | { readonly kind: "styleBundle"; readonly decl: StyleBundleDecl }
  | { readonly kind: "lens"; readonly decl: LensDecl }
  | { readonly kind: "decoration"; readonly decl: DecorationDecl }
  | { readonly kind: "usePolicy"; readonly decl: UsePolicyStmt };

/**
 * `in <Target> { … }` extension block (§4.16). Reopens an already-declared
 * module or surface and merges the block's body members into it (set union).
 * The resolver merges `body` into the target's body; a missing target is a hard
 * `IN_TARGET_NOT_FOUND`. (The single form `in <Target> <decl>` is represented
 * instead as the placed decl carrying `parentPath = target`.)
 */
export interface ExtensionDecl {
  readonly target: QualifiedName;
  readonly body: Body;
  readonly source?: SourceSpan;
  readonly targetSource?: SourceSpan;
}

/**
 * `use` declaration — imports types from another package.
 *
 * Forms:
 *   `use Type from pkg.path`              one type, no rename
 *   `use Type as Alias from pkg.path`     one type with rename
 *   `use A, B as C from pkg.path`         batched
 *   `use * from pkg.path`                 wildcard (only exported types)
 *
 * Scope depends on where the declaration appears:
 *   - In `package.archspace`: visible to every file in the package.
 *   - In a `.arch` file: visible only inside that file. Other files in the
 *     same package must declare their own `use` to see the same name.
 * The package-resolver tags each import with its source-file URI (or a
 * "manifest" marker) and emits TYPE_NOT_VISIBLE_IN_FILE for cross-file
 * leakage.
 */
export interface UseDecl {
  /** The package being imported from, as a dotted path (`arch.web.micro`). */
  readonly fromPackage: QualifiedName;
  /** True for `use * from pkg`. */
  readonly wildcard: boolean;
  /** Specific imports (empty when wildcard). */
  readonly imports: readonly UseImport[];
  readonly source?: SourceSpan;
  readonly fromSource?: SourceSpan;
  /** True for `export use ... from pkg` — re-exports the imported names. */
  readonly reExport?: boolean;
}

export interface UseImport {
  readonly name: Name;
  readonly alias?: Name;
  readonly source?: SourceSpan;
  readonly nameSource?: SourceSpan;
  readonly aliasSource?: SourceSpan;
}

export interface ParsedFile {
  readonly declarations: readonly TopLevelDecl[];
  readonly uses: readonly UseDecl[];
  /** v0.9.0 §1.2: bare references inside top-level `on <plane> { … }` blocks —
   *  plane inclusions for modules declared elsewhere. */
  readonly onInclusions?: readonly OnInclusion[];
  readonly source?: SourceSpan;
}

// =============================================================================
// Package manifest (parsed from package.archspace)
// =============================================================================

/**
 * The contents of a `package.archspace` file. Every workspace has at most
 * one of these at its root.
 *
 * Required: `name`. Everything else is optional.
 */
export interface PackageManifest {
  /**
   * Package identifier as a dotted path (`arch.web.micro`, `acme.shop`),
   * declared by `package:`. Present on a **package-root** manifest; absent
   * on a **space** manifest (which carries {@link spaceName} instead). A
   * manifest declares exactly one of `package` or `spaceName`.
   */
  readonly package?: QualifiedName;
  readonly packageSource?: SourceSpan;
  /**
   * Space name as a dotted path (`Corporate.Loans`), declared by `name:`.
   * Present on a **space** manifest — a visibility boundary within the
   * enclosing package (v0.5). Absent on a package-root manifest.
   */
  readonly spaceName?: QualifiedName;
  readonly spaceNameSource?: SourceSpan;
  /** Optional semver-style version string. Free-form for now; no parsing. */
  readonly version?: string;
  readonly versionSource?: SourceSpan;
  /**
   * Optional path (relative to the manifest dir) to a JS/TS module that
   * registers custom elements via `customElements.define()`. The viewer
   * loads each package's widgets module on package init; types in the
   * package can then reference its element tags via the `widget` field.
   */
  readonly widgets?: string;
  readonly widgetsSource?: SourceSpan;
  /**
   * Origin URL of the code repository this package's modules bind evidence to
   * (§11.1), with {@link commit} pinning the revision those bindings are true
   * of. The pin is package-scoped because a `sources:` entry names a path
   * relative to one repository; scoping it here is what makes a bare binding
   * unambiguous no matter how many dependencies the package has.
   */
  readonly repo?: string;
  readonly repoSource?: SourceSpan;
  /** The revision `repo`'s evidence bindings are pinned to. Required whenever
   *  `repo` is set: an unpinned path attests that a file existed once, not that
   *  the model is true of a commit. */
  readonly commit?: string;
  readonly commitSource?: SourceSpan;
  /**
   * Package-level description. Bare-string members at the manifest root,
   * concatenated with '\n' like a module's. Supports markdown, `[[refs]]`
   * and `@aspects` via {@link renderDescription}, same as module descriptions.
   */
  readonly descriptions: readonly DescriptionEntry[];
  /** Map from dotted package name to filesystem path (relative to manifest dir). */
  readonly dependencies: readonly DependencyEntry[];
  /** Package-global imports (apply to every .arch file in the package). */
  readonly uses: readonly UseDecl[];
  /** Governance packs this package REQUIRES, each pinned to a version — the
   *  `policies { <pkg>: "<version>" }` manifest block. Adopting a pack here is
   *  what makes it un-droppable: the gate fails if a required pack's exported
   *  policies stop being imported, so a `use policy` can't be deleted to go
   *  green. Empty (the default) = no pack adopted, no pack gate. */
  readonly policyPacks: readonly PolicyPackRequirement[];
  readonly source?: SourceSpan;
}

/** One adopted governance pack: the package that ships it and the version the
 *  consumer pins. `version` is compared verbatim against the pack's own
 *  manifest `version:` — a mismatch fails the gate rather than silently
 *  enforcing a different rule set. */
export interface PolicyPackRequirement {
  readonly name: QualifiedName;
  readonly version: string;
  readonly source?: SourceSpan;
  readonly nameSource?: SourceSpan;
  readonly versionSource?: SourceSpan;
}

export interface DependencyEntry {
  readonly name: QualifiedName;
  readonly path: string;
  readonly source?: SourceSpan;
  readonly nameSource?: SourceSpan;
  readonly pathSource?: SourceSpan;
}

// =============================================================================
// Architecture model (resolved, post-validation)
// =============================================================================

/**
 * The complete, resolved architecture model. This is the output of the
 * resolver — type chains have been walked, auto-propagation has stamped
 * type defaults onto instances, cascade has been applied, and validation
 * has run. Consumers (graph builder, diagram renderer, view projector)
 * read from this.
 *
 * For now this is a thin wrapper around the parsed declarations until
 * the resolver lands.
 */
export interface ArchitectureModel {
  readonly types: readonly TypeDecl[];
  readonly modules: readonly ModuleDecl[];
  readonly processes: readonly ProcessDecl[];
  readonly subprocesses: readonly SubprocessDecl[];
  readonly views: readonly ViewDeclV2[];
}

// =============================================================================
// Diagnostics
// =============================================================================

/**
 * `todo` is a first-class third class (v0.6): *missing detail* — the model is
 * incomplete but consistent (a local reference not defined yet, auto-stubbed
 * per SPECIFICATION.md §4.13). Distinct from `error` (contradiction / invalid)
 * and `warning` (discouraged-but-tolerated). Tooling can count TODOs as draft
 * debt and gate propose/accept on zero; draft/preview surfaces tolerate them.
 */
export type DiagnosticSeverity = "error" | "warning" | "todo" | "info" | "hint";

/** A secondary location attached to a diagnostic (e.g. the policy rule that
 *  flagged a violation) — surfaced as clickable related information in editors. */
export interface DiagnosticRelated {
  readonly source: SourceSpan;
  readonly message: string;
}

export interface Diagnostic {
  readonly severity: DiagnosticSeverity;
  /** English rendering — always present, the fallback when `msgKey` is unset or
   *  uncatalogued. Consumers that localise render from `msgKey`+`params` and use
   *  this only as a fallback. */
  readonly message: string;
  readonly source?: SourceSpan;
  /** Registered code from the closed registry (diagnostics/codes.ts) — what a
   *  consumer keys repairs and suppression on. Never a free string. */
  readonly code?: DiagnosticCode;
  /** Localisation key into the diagnostic catalog (diagnostics/catalog.ts).
   *  When set and catalogued, each consumer renders the message in its own
   *  locale via `renderDiagnostic(msgKey, params, locale)`. */
  readonly msgKey?: string;
  /** Structured interpolation values for `msgKey` (names, counts, lists). Also
   *  what quick-fixes should read, instead of parsing the rendered message. */
  readonly params?: Readonly<Record<string, unknown>>;
  /** Extra locations that help explain the diagnostic (optional). */
  readonly relatedInformation?: readonly DiagnosticRelated[];
  /** The model facts that PROVE the finding — the nodes/edges a reader (or an
   *  agent) re-checks without re-running the analysis. Structured, never prose. */
  readonly evidence?: readonly DiagnosticEvidence[];
}

// =============================================================================
// Factory helpers
// =============================================================================

let idCounter = 0;

/**
 * Generate a deterministic, opaque stable ID. Prefixed with a few
 * characters from base36 of an internal counter; suitable for fixtures
 * and for the formatter's mint-on-save fallback when no deterministic
 * derivation is available.
 */
export function generateId(): StableId {
  return ((++idCounter).toString(36).padStart(6, "0")) as StableId;
}

/** Wrap an opaque suffix as a StableId (used by the parser). */
export function stableId(suffix: string): StableId {
  return suffix as StableId;
}

/**
 * Phase-1 guard for the v0.8 process language. The new step kinds
 * (`reversible`, `await`, `go`, `owner_block`) parse into the AST but their
 * graph / validation / stub / render / index semantics land in later phases
 * (the consumers will be rewritten to read the resolved `ProcessModel` IR).
 * Until then, every raw-AST `ProcessStep` walker calls this for those kinds so
 * the gap is loud rather than silent. (`select` replaces the v0.7 `switch` and
 * IS handled; `each` retry tails and the reshaped `parallel`/`if` are handled
 * inline by the existing kinds.)
 */
export function processStepNotImplemented(step: { readonly kind: string }): never {
  throw new Error(`${step.kind}: not implemented until v0.8 Phase 3`);
}

// =============================================================================
// Convenience helpers
// =============================================================================

/** Concatenate description entries with newlines (per body-semantics rule). */
export function joinDescriptions(entries: readonly DescriptionEntry[]): string {
  return entries.map((e) => e.text).join("\n");
}

/** Render a qualified name back to its dot-string form. */
export function qualifiedNameToString(qname: QualifiedName): string {
  return qname.join(".");
}
/**
 * Fully-qualified process name: `<owner-path>.<name>` for module-scoped
 * processes, plain `<name>` for free-standing ones. Use this as the
 * lookup key wherever short names could collide (sidebar registration,
 * URL params, graph edges, indexes). The short `decl.name` remains the
 * display label.
 */
/**
 * Name of the synthesized interface that stands in for an unspecified
 * callee leaf (§4.13.2 / §10.3). The parentheses make it an
 * unauthorable identifier (the lexer never produces it), so it can
 * never collide with a real interface name, and the absence of a dot
 * keeps qualified-path splitting unambiguous.
 */
export const ANONYMOUS_INTERFACE_NAME = "(anonymous)";

export function processQualifiedName(p: ProcessDecl): string {
  return p.ownerPath && p.ownerPath.length > 0
    ? `${p.ownerPath.join(".")}.${p.name}`
    : p.name;
}

/**
 * Stable disambiguating identity for a process — what index keys + graph-edge
 * attribution use so two distinct processes never collide. A `#id` or a name
 * (qualified) is unique; an ANONYMOUS process (a bare-step run, name `""`) has no
 * such handle, and a model may hold several (split by an intervening decl,
 * §4.13.1). Without disambiguation they'd all key to `""`/`Owner.` — collapsing
 * in the model index and merging their edges so playback highlights them all at
 * once. So an anonymous process is keyed by a CONTENT signature (its ordered
 * call sites): position-independent (cold == incremental), unique per distinct
 * bare-step run. (Two byte-identical anon runs still share a key — a benign
 * degenerate case.)
 */
export function processIdentity(p: ProcessDecl): string {
  if (p.id) return p.id;
  // v0.11 (D2): the landed space prefixes the fallback identity — two
  // spaces' same-named processes are distinct entities. Spaceless processes
  // keep the byte-identical pre-v0.11 key.
  const qn =
    p.space && p.space.length > 0
      ? `${p.space.join(".")}::${processQualifiedName(p)}`
      : processQualifiedName(p);
  if (p.name !== "") return qn; // named, no #id — the qualified name is unique
  return `${qn}(anon:${stepsSignature(p.steps)})`;
}

/**
 * Complete content signature of a step subtree: a hash of its structure with
 * source spans removed. Derived from the FULL serialized steps (not a
 * hand-picked field list — that approach repeatedly missed fields: owners on
 * containers, merge clauses, select counts, reversible triggers, `as`/template
 * callers, …), so it captures EVERY discriminating field by construction. Spans
 * are stripped with the SAME predicate the incremental-equivalence oracle uses,
 * so the signature is identical between a per-file (incremental) and merged
 * (cold) parse — keeping anon-process ids cold==incremental.
 */
function stepsSignature(steps: readonly ProcessStep[]): string {
  const json = JSON.stringify(steps, (k, v) =>
    k === "source" ||
    k.endsWith("Source") ||
    k === "span" ||
    k === "start" ||
    k === "end" ||
    k === "offset" ||
    k === "line" ||
    k === "column" ||
    k === "file"
      ? undefined
      : v
  );
  // FNV-1a → short base36 key; collision-negligible for the handful of anon
  // processes in a model, and far more compact than the raw JSON on every edge.
  let h = 0x811c9dc5;
  for (let i = 0; i < json.length; i++) {
    h ^= json.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/**
 * Fully-qualified subprocess name: `<owner-path>.<name>` (owner is
 * either an owning process's qualified name or an owning module's
 * qualified path), plain `<name>` for free-standing subprocesses.
 */
export function subprocessQualifiedName(sp: SubprocessDecl): string {
  return sp.ownerPath && sp.ownerPath.length > 0
    ? `${sp.ownerPath.join(".")}.${sp.name}`
    : sp.name;
}
